import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bandeau } from './Champs'
import { dateCourte } from './Messagerie'
import { ErreurApi } from '../api'
import {
  compterNonLues,
  listerNotifications,
  marquerNotificationsLues,
  type Notification,
} from '../apiRelations'
import { TEXTES, messageErreur, type Langue } from '../i18n'
import { Bloc, Bouton, Compteur, EtatVide, Icone, Puce, SqueletteListe, type NomIcone } from '../ui'

/**
 * Centre de notifications — L4, repris sur sa maquette le 14/09/2026.
 *
 * **C'est ce qui rend la messagerie utilisable.** Sans lui, qui reçoit un message ne le sait pas,
 * ne revient pas, et poursuit sur WhatsApp — et aucun masquage de coordonnées ne compenserait
 * cela. C'est pourquoi il a été avancé de L9 à ce lot (conception L4, §1.5).
 *
 * **La notification ne porte toujours aucun contenu** — un type et des identifiants, rien de plus
 * (modèle §9). La phrase de détail de la maquette existe pourtant depuis le 14/09/2026 : elle est
 * **relue à la source** par l'API à chaque requête, avec les droits du demandeur et le masquage du
 * fil, et non recopiée dans la notification à sa création. C'est la différence entre afficher un
 * message et en garder une seconde copie à masquer, à purger et à anonymiser.
 *
 * **Pull, jamais push** (D9) : le compteur se relit au chargement d'écran. Le canal de remise
 * arrivera avec L1c ; ces mêmes lignes lui serviront de source.
 */
export function Notifications({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]

  const [liste, setListe] = useState<Notification[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(() => {
    void listerNotifications()
      .then((page) => setListe(page.items))
      .catch((cause: unknown) => {
        setListe([])
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      })
  }, [langue])

  useEffect(charger, [charger])

  const toutMarquer = useCallback(async () => {
    const ids = (liste ?? []).filter((item) => item.readAt === null).map((item) => item.id)

    if (ids.length === 0) {
      return
    }

    try {
      await marquerNotificationsLues(ids)
      signalerLecture()
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [charger, langue, liste])

  const nonLues = (liste ?? []).some((item) => item.readAt === null)

  return (
    <section className="notifications">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.notifications}</h2>

          {/* Le geste est dans la rangée du titre, comme sur la maquette, et il disparaît quand
              il n'y a rien à marquer — un bouton toujours présent mais sans effet apprend à ne
              plus le lire. */}
          {nonLues && (
            <Bouton apparence="discret" taille="sm" onClick={() => void toutMarquer()}>
              {t.toutMarquerLu}
            </Bouton>
          )}
        </div>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {liste === null && <SqueletteListe />}
      {liste !== null && liste.length === 0 && erreur === null && (
        <EtatVide icone="cloche" titre={t.aucuneNotification} />
      )}

      {liste !== null && liste.length > 0 && (
        <Bloc variante="nu">
          {liste.map((notification) => (
            <LigneNotification key={notification.id} notification={notification} langue={langue} />
          ))}
        </Bloc>
      )}

      <CanauxDeRemise langue={langue} />
    </section>
  )
}

/**
 * Une ligne : une icône, un libellé, un aperçu, une date. **La ligne entière est le lien**, et non
 * un mot à la fin — une cible de 44 px de haut se touche du pouce, un lien de trois lettres non.
 */
function LigneNotification({
  notification,
  langue,
}: {
  notification: Notification
  langue: Langue
}) {
  const t = TEXTES[langue]
  const genre = GENRES[notification.eventType]
  const cible = destination(notification)

  const contenu = (
    <>
      <span className="notification__icone">
        <Icone nom={genre?.icone ?? 'cloche'} taille="sm" />
      </span>
      <span className="notification__corps">
        <span className="fort">{libelle(t, notification.eventType)}</span>

        {/* L'aperçu ne s'affiche que s'il dit quelque chose : une ligne vide sous le titre
            décalerait la date sans rien apprendre. */}
        {notification.preview !== null && (
          <span className="secondaire notification__apercu">{notification.preview}</span>
        )}

        <time className="notification__date" dateTime={notification.createdAt}>
          {ilYA(langue, notification.createdAt)}
        </time>
      </span>
    </>
  )

  const classe = notification.readAt === null ? 'notification est-non-lue' : 'notification'

  // Une notification sans destination reste une ligne inerte : un lien qui ne mène nulle part est
  // pire qu'un texte, parce qu'il se clique.
  return cible === null ? (
    <div className={classe}>{contenu}</div>
  ) : (
    <Link className={classe} to={cible} onClick={() => marquerLue(notification)}>
      {contenu}
    </Link>
  )
}

/**
 * Ouvrir une notification la marque lue — <b>sans faire attendre la navigation</b>.
 *
 * **Le défaut corrigé** (relevé le 15/09/2026) : on cliquait, on arrivait bien sur la conversation,
 * et la notification restait en gras avec la pastille inchangée. Il fallait revenir sur l'écran et
 * appuyer sur « tout marquer comme lu » pour solder ce qu'on venait justement de lire — soit
 * exactement le geste que la notification existe pour éviter.
 *
 * **Lu au clic, et non à l'arrivée sur la destination.** C'est le geste d'ouverture qui dit « j'ai
 * vu », pas l'affichage du fil : la même notification peut mener à un écran qu'on visite par
 * ailleurs dix fois par jour, et marquer là-bas solderait des notifications qu'on n'a jamais
 * ouvertes. C'est déjà la règle du client mobile, qui marque dans son `ouvrir`.
 *
 * **Sans `await`, et l'échec est silencieux.** La navigation part dans le même geste ; faire
 * attendre la requête ajouterait une latence visible pour un confort. Si l'appel échoue, la ligne
 * reste non lue — une notification lue qu'on redécouvre est une gêne, une notification perdue
 * serait une panne.
 */
function marquerLue(notification: Notification): void {
  if (notification.readAt !== null) {
    return
  }

  void marquerNotificationsLues([notification.id])
    .then(signalerLecture)
    .catch(() => {
      /* la ligne restera non lue : rien de plus honnête que de ne pas mentir sur l'état */
    })
}

/**
 * Qui veut savoir que des notifications viennent d'être lues.
 *
 * **Pourquoi un abonnement et non une prop.** Le compteur vit dans l'en-tête, qui est monté une
 * fois pour toute l'application ; ce qui marque une notification lue est ailleurs — l'écran, la
 * pastille de la cloche, et demain le fil lui-même. Faire descendre un `onLu` à travers la
 * coquille obligerait chaque écran intermédiaire à transporter une prop qui ne le concerne pas.
 *
 * Le compteur reste **relu, jamais poussé** (D29) : le signal ne transporte aucune valeur, il dit
 * seulement qu'il est temps de redemander. Une soustraction locale se désynchroniserait du jour où
 * deux onglets sont ouverts.
 */
const auditeursDeLecture = new Set<() => void>()

function signalerLecture(): void {
  for (const auditeur of auditeursDeLecture) {
    auditeur()
  }
}

/**
 * « Comment vous êtes prévenu » — **en lecture seule, et c'est le point**.
 *
 * La maquette montre trois interrupteurs. Aucun réglage de canal n'est stocké : les proposer
 * ferait basculer un état que personne ne relit, ce qui est pire qu'une information honnête. Le
 * canal dans l'application est le seul livré ; l'e-mail et le Web Push existent dans l'énumération
 * `NotificationChannelKind` et nulle part ailleurs.
 */
function CanauxDeRemise({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]

  const canaux: { libelle: string; disponible: boolean }[] = [
    { libelle: t.notifCanalApplication, disponible: true },
    { libelle: t.notifCanalEmail, disponible: false },
    { libelle: t.notifCanalPush, disponible: false },
  ]

  return (
    <Bloc variante="sourd" titre={t.notifCanauxTitre}>
      <ul className="liste-lignes">
        {canaux.map((canal) => (
          <li key={canal.libelle}>
            <span className="liste-lignes__corps">{canal.libelle}</span>
            <Puce ton={canal.disponible ? 'succes' : undefined}>
              {canal.disponible ? t.notifCanalActif : t.notifCanalIndisponible}
            </Puce>
          </li>
        ))}
      </ul>

      <p className="aide notifications__canaux-aide">{t.notifCanauxAide}</p>
    </Bloc>
  )
}

/**
 * Le compteur de non-lus : relu au montage, et **relu à chaque lecture signalée**.
 *
 * Il tolère l'échec en silence : une pastille absente est une gêne, un écran qui refuse de
 * s'afficher parce qu'un compteur n'a pas répondu est une panne.
 */
export function useNonLues(connecte: boolean): number {
  const [compte, setCompte] = useState(0)

  const relire = useCallback(() => {
    if (!connecte) {
      setCompte(0)
      return
    }

    void compterNonLues()
      .then((reponse) => setCompte(reponse.unread))
      .catch(() => setCompte(0))
  }, [connecte])

  useEffect(() => {
    relire()

    // L'abonnement est ce qui fait tomber la pastille quand on ouvre une notification : sans lui,
    // elle afficherait 33 jusqu'au prochain rechargement de page, en contredisant la liste juste
    // en dessous.
    auditeursDeLecture.add(relire)

    return () => {
      auditeursDeLecture.delete(relire)
    }
  }, [relire])

  return compte
}

/**
 * Le catalogue des types d'événement — **un libellé, une icône, une destination par famille**.
 *
 * **Pourquoi une table et non une suite de `case`.** Le `switch` qu'elle remplace ne couvrait que
 * huit types sur la trentaine que le produit écrit : tout le reste tombait sur le libellé par
 * défaut, et l'écran affichait treize lignes intitulées « Notifications » — une liste qui ne dit
 * rien de ce qu'elle annonce. Une table se compare d'un coup d'œil avec les noms d'événement du
 * domaine, ce qu'une cascade de `case` ne permet pas.
 *
 * Les clés sont les noms portés par `DomainEventNameAttribute`, plus les cinq types que les jobs
 * du Worker écrivent directement. Les recopier ici est un couplage assumé : c'est la frontière
 * entre un événement et une phrase, et elle doit bien être quelque part.
 */
interface GenreNotification {
  cle: keyof (typeof TEXTES)['fr']
  icone: NomIcone
  /** Où mène la ligne quand la notification ne porte aucun identifiant exploitable. */
  vers: string | null
}

const GENRES: Record<string, GenreNotification> = {
  'messaging.message_sent': { cle: 'notifMessage', icone: 'message', vers: '/messages' },
  'messaging.conversation_started': { cle: 'notifConversation', icone: 'message', vers: '/messages' },

  'seeking.application_submitted': { cle: 'notifCandidature', icone: 'fichier', vers: '/mes-candidatures' },
  'seeking.application_accepted': { cle: 'notifAcceptee', icone: 'verifie', vers: '/mes-candidatures' },
  'seeking.application_rejected': { cle: 'notifRefusee', icone: 'croix', vers: '/mes-candidatures' },
  'seeking.application_expired': { cle: 'notifCandidatureExpiree', icone: 'horloge', vers: '/mes-candidatures' },
  'seeking.visit_proposed': { cle: 'notifVisiteProposee', icone: 'calendrier', vers: '/visites' },
  'seeking.visit_confirmed': { cle: 'notifVisiteConfirmee', icone: 'calendrier', vers: '/visites' },
  'seeking.visit_completed': { cle: 'notifVisiteFaite', icone: 'verifie', vers: '/visites' },
  'seeking.visit_reminder': { cle: 'notifRappelVisite', icone: 'horloge', vers: '/visites' },
  'seeking.household_vote_opened': { cle: 'notifVoteOuvert', icone: 'groupe', vers: '/foyer' },
  'seeking.household_vote_closed': { cle: 'notifVoteClos', icone: 'groupe', vers: '/foyer' },

  'leasing.lease_signed': { cle: 'notifBailSigne', icone: 'cle', vers: '/mes-baux' },
  'leasing.lease_terminated': { cle: 'notifBailTermine', icone: 'cle', vers: '/mes-baux' },
  'leasing.rent_payment_confirmed': { cle: 'notifLoyerConfirme', icone: 'monnaie', vers: '/echeances' },
  'leasing.rent_installment_overdue': { cle: 'notifLoyerEnRetard', icone: 'alerte', vers: '/echeances' },
  'leasing.rent_reminder': { cle: 'notifRappelLoyer', icone: 'monnaie', vers: '/echeances' },
  'leasing.declaration_expired': { cle: 'notifDeclarationExpiree', icone: 'horloge', vers: '/echeances' },

  'household.daily_digest': { cle: 'notifDigestFoyer', icone: 'tache', vers: '/foyer' },
  'household.house_rules_published': { cle: 'notifReglement', icone: 'livre', vers: '/foyer' },
  'household.settlement_proposed': { cle: 'notifSoldePropose', icone: 'balance', vers: '/foyer' },
  'household.settlement_confirmed': { cle: 'notifSoldeConfirme', icone: 'balance', vers: '/foyer' },
  'household.departure_announced': { cle: 'notifDepartAnnonce', icone: 'sortie', vers: '/foyer' },
  'household.member_left': { cle: 'notifDepart', icone: 'sortie', vers: '/foyer' },

  'reputation.reviews_requested': { cle: 'notifAvisDemande', icone: 'etoile', vers: '/avis' },
  'reputation.review_received': { cle: 'notifAvisRecu', icone: 'etoile', vers: '/avis' },
  'reputation.counterpart_submitted': { cle: 'notifAvisReciproque', icone: 'etoile', vers: '/avis' },
  'reputation.review_contested': { cle: 'notifAvisConteste', icone: 'balance', vers: '/avis' },
  'reputation.review_hidden': { cle: 'notifAvisMasque', icone: 'oeil', vers: '/avis' },
  'reputation.review_removed': { cle: 'notifAvisRetire', icone: 'poubelle', vers: '/avis' },
}

/** Le type d'événement devient du texte ICI, et nulle part ailleurs (invariant 11). */
export function libelle(t: (typeof TEXTES)['fr'], type: string): string {
  const genre = GENRES[type]

  // Un type inconnu — un lot plus récent que cet écran — rend « Notification » au singulier, et
  // non le titre de la page : la ligne dit alors au moins qu'elle est une notification isolée.
  return genre === undefined ? t.notifAutre : t[genre.cle]
}

/**
 * Où mène la ligne.
 *
 * Les identifiants portés par la notification l'emportent : ils mènent à l'objet exact. La
 * destination de famille ne sert qu'à ceux qui n'en portent aucun — un rappel de loyer, un
 * règlement publié —, et vaut mieux qu'une ligne inerte.
 */
export function destination(notification: Notification): string | null {
  if (notification.conversationId !== null) {
    return `/messages/${notification.conversationId}`
  }

  if (notification.applicationId !== null) {
    return '/mes-candidatures'
  }

  if (notification.listingId !== null) {
    return `/annonces/${notification.listingId}`
  }

  return GENRES[notification.eventType]?.vers ?? null
}

/**
 * « Il y a 12 minutes », « hier », puis une date.
 *
 * `Intl.RelativeTimeFormat` et non un dictionnaire : c'est lui qui sait que l'arabe ne forme pas
 * « il y a deux jours » comme « il y a trois jours ». Au-delà d'une semaine, le relatif cesse
 * d'informer — « il y a 23 jours » ne se situe pas — et la date courte reprend la main.
 */
function ilYA(langue: Langue, iso: string): string {
  const minutes = (Date.now() - new Date(iso).getTime()) / 60000

  if (!Number.isFinite(minutes) || minutes < 0) {
    return dateCourte(langue, iso)
  }

  const relatif = new Intl.RelativeTimeFormat(langue, { numeric: 'auto' })

  if (minutes < 1) return relatif.format(0, 'minute')
  if (minutes < 60) return relatif.format(-Math.round(minutes), 'minute')
  if (minutes < 1440) return relatif.format(-Math.round(minutes / 60), 'hour')
  if (minutes < 10080) return relatif.format(-Math.round(minutes / 1440), 'day')

  return dateCourte(langue, iso)
}

/**
 * La cloche de l'en-tête — pastille de compteur, et les dix dernières notifications au clic.
 *
 * **Pourquoi une pastille et pas seulement un lien** (ticket `fo-transverse-03`). La cloche
 * annonçait qu'il se passait quelque chose sans jamais dire quoi : il fallait quitter l'écran en
 * cours pour l'apprendre, puis y revenir. Dix lignes suffisent à trancher entre « cela peut
 * attendre » et « j'y vais » — et c'est exactement la décision que la cloche existe pour servir.
 *
 * **Dix, et un lien vers le reste.** Une pastille qui déroulerait tout deviendrait un second écran
 * de notifications, en moins bon : sans pagination, sans « tout marquer comme lu », et dans une
 * boîte de 22 rem de large.
 *
 * **Elle emprunte l'anatomie de l'écran** (14/09/2026) : la même icône, le même libellé, le même
 * aperçu, la même date relative. Deux rendus différents pour la même donnée obligeaient à traduire
 * deux fois — et l'un des deux affichait encore « Notifications » sur chaque ligne.
 *
 * **La liste n'est chargée qu'à l'ouverture**, jamais au montage de l'en-tête : sinon chaque page
 * de l'application paierait une requête que personne n'a demandée. Le compteur, lui, est déjà là —
 * il vient de `useNonLues`, qui coûte une seule ligne de réponse.
 */
export function ClocheNotifications({ langue, nonLues }: { langue: Langue; nonLues: number }) {
  const t = TEXTES[langue]
  const [ouverte, setOuverte] = useState(false)
  const [dernieres, setDernieres] = useState<Notification[] | null>(null)
  const racine = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ouverte) {
      return
    }

    // Rechargé à chaque ouverture et non mis en cache : entre deux ouvertures, l'intérêt de la
    // pastille est précisément ce qui a changé.
    setDernieres(null)

    void listerNotifications(1, 10)
      .then((page) => setDernieres(page.items))
      .catch(() => setDernieres([]))

    function surClic(evenement: MouseEvent) {
      if (racine.current !== null && !racine.current.contains(evenement.target as Node)) {
        setOuverte(false)
      }
    }

    function surTouche(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') {
        setOuverte(false)
      }
    }

    document.addEventListener('mousedown', surClic)
    document.addEventListener('keydown', surTouche)

    return () => {
      document.removeEventListener('mousedown', surClic)
      document.removeEventListener('keydown', surTouche)
    }
  }, [ouverte])

  return (
    <div className="cloche" ref={racine}>
      <button
        type="button"
        className="entete__icone"
        aria-label={t.notifications}
        aria-expanded={ouverte}
        aria-haspopup="true"
        onClick={() => setOuverte(!ouverte)}
      >
        <Icone nom="cloche" />
        <Compteur valeur={nonLues} langue={langue} />
      </button>

      <div className="cloche__panneau" hidden={!ouverte}>
        <p className="cloche__titre">{t.notificationsRecentes}</p>

        {dernieres === null && <p className="cloche__vide">{t.chargement}</p>}
        {dernieres !== null && dernieres.length === 0 && (
          <p className="cloche__vide">{t.aucuneNotification}</p>
        )}

        {dernieres !== null && dernieres.length > 0 && (
          <ul className="cloche__liste">
            {dernieres.map((notification) => {
              const cible = destination(notification)
              const genre = GENRES[notification.eventType]
              const contenu = (
                <>
                  <span className="cloche__icone">
                    <Icone nom={genre?.icone ?? 'cloche'} taille="sm" />
                  </span>
                  <span className="cloche__corps">
                    <span className="cloche__libelle">{libelle(t, notification.eventType)}</span>

                    {/* Le même aperçu que l'écran, mais sur UNE ligne : la pastille fait 22 rem,
                        et trois lignes par entrée y feraient dix entrées illisibles. */}
                    {notification.preview !== null && (
                      <span className="cloche__apercu">{notification.preview}</span>
                    )}
                  </span>
                  <time className="cloche__date" dateTime={notification.createdAt}>
                    {ilYA(langue, notification.createdAt)}
                  </time>
                </>
              )

              return (
                <li
                  key={notification.id}
                  className={notification.readAt === null ? 'cloche__ligne est-nouvelle' : 'cloche__ligne'}
                >
                  {cible === null ? (
                    <span className="cloche__entree">{contenu}</span>
                  ) : (
                    <Link
                      className="cloche__entree"
                      to={cible}
                      onClick={() => {
                        setOuverte(false)
                        marquerLue(notification)
                      }}
                    >
                      {contenu}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <Link className="cloche__toutes" to="/notifications" onClick={() => setOuverte(false)}>
          {t.notificationsVoirToutes}
        </Link>
      </div>
    </div>
  )
}
